import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ADMIN_EXPEDIENTE_URL, EXPEDIENTES_URL } from '../constants';

const button = 'rounded-lg px-4 py-2 font-medium border disabled:opacity-50 disabled:cursor-not-allowed';
const states = { queued: 'En cola', running: 'En ejecución', receiving: 'Recibiendo inventario', awaiting_transfer: 'Esperando transferencia desde Apps Script', paused: 'Pausado', failed: 'Fallido', completed: 'Terminado',
  completed_with_errors: 'Terminado con errores', pending: 'Pendiente', available: 'Indexado', indexing: 'Indexando',
  uploaded: 'Subido, pendiente de indexación', error: 'Error de documento', upload_uncertain: 'Subida pendiente de conciliación',
  excluded: 'Excluido', deferred: 'Diferido', unsupported: 'No soportado', folder: 'Carpeta' };
const reasons = { PDF_ONLY_PHASE: 'Esta fase carga solo PDFs', UNSUPPORTED_FILE_TYPE: 'Formato no soportado',
  EMPTY_FILE: 'Archivo vacío', FILE_TOO_LARGE: 'Supera el tamaño permitido', DOWNLOAD_NOT_ALLOWED: 'Descarga no autorizada',
  SHORTCUT_NOT_FOLLOWED: 'Acceso directo sin recorrer', EXPORT_NOT_IMPLEMENTED: 'Requiere exportación',
  SOURCE_CHANGED_AFTER_PREVIEW: 'Cambió desde el inventario', VERSION_REPLACEMENT_REQUIRES_REVIEW: 'Reemplazo de versión pendiente de revisión',
  waiting_for_indexing: 'Esperando indexación en OpenAI' };
const label = value => states[value] || value;
const reason = value => reasons[value] || value;
const active = job => ['queued', 'running', 'receiving', 'awaiting_transfer', 'paused'].includes(job.status);
const safeDriveUrl = value => {
  try { const url = new URL(value); return url.protocol === 'https:' && ['drive.google.com', 'docs.google.com'].includes(url.hostname)
    && !url.username && !url.password ? url.href : null; } catch { return null; }
};

export default function DriveAdministration({ token, onDenied }) {
  const [discovery, setDiscovery] = useState(null);
  const [config, setConfig] = useState(null);
  const [connected, setConnected] = useState(false);
  const [transport, setTransport] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [page, setPage] = useState({ documents: [], counts: [], nextCursor: null });
  const [pageLoading, setPageLoading] = useState(true);
  const [cursors, setCursors] = useState(['']);
  const [index, setIndex] = useState(0);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);
  const [partial, setPartial] = useState(false);
  const [maxDocuments, setMaxDocuments] = useState(10);
  const [target, setTarget] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const mutation = useRef(false);
  const mounted = useRef(true);
  const base = discovery?.caseId ? `${EXPEDIENTES_URL}/${encodeURIComponent(discovery.caseId)}` : null;
  const options = useCallback(signal => ({ headers: { Authorization: `Bearer ${token}` }, ...(signal ? { signal } : {}) }), [token]);
  const report = useCallback(err => {
    if (err.response?.status === 401) onDenied(401);
    else if (err.response?.status === 403) setDenied(true);
    else setError(err.response?.data?.error || 'No se pudo completar la operación. Actualiza el estado antes de reintentar.');
  }, [onDenied]);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(''); setDenied(false);
    axios.get(ADMIN_EXPEDIENTE_URL, options(controller.signal)).then(async response => {
      if (controller.signal.aborted) return;
      setDiscovery(response.data);
      if (!response.data.enabled) return;
      const url = `${EXPEDIENTES_URL}/${encodeURIComponent(response.data.caseId)}`;
      const summary = await axios.get(url, options(controller.signal));
      if (controller.signal.aborted) return;
      if (!summary.data.permissions.canAdminister) { setDenied(true); return; }
      const settings = await axios.get(`${url}/administracion/configuracion`, options(controller.signal));
      if (!controller.signal.aborted) setConfig(settings.data);
    }).catch(err => { if (!controller.signal.aborted) report(err); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [options, report, attempt]);

  const ready = Boolean(base && config && discovery.syncEnabled && !denied && !loading);
  // Cada ciclo termina antes del siguiente; abortar evita mezclar páginas o sesiones.
  useEffect(() => {
    if (!ready) return undefined;
    const controller = new AbortController();
    let timer;
    const refresh = async () => {
      setPageLoading(true);
      const results = await Promise.allSettled([
        axios.get(`${base}/drive`, options(controller.signal)),
        axios.get(`${base}/trabajos`, options(controller.signal)),
        axios.get(`${base}/documentos`, { ...options(controller.signal), params: { limit: 25, after: cursors[index], status: filter } }),
      ]);
      if (controller.signal.aborted) return;
      results.forEach((result, i) => {
        if (result.status === 'rejected') { report(result.reason); return; }
        const data = result.value.data;
        if (i === 0) { setConnected(data.connected); setTransport(data.transport || 'google_oauth'); }
        if (i === 1) { setJobs(data.jobs); setPreview(data.latestInventory || null); }
        if (i === 2) setPage(data);
      });
      setPageLoading(false);
      timer = setTimeout(refresh, 5000);
    };
    refresh();
    const focus = () => setAttempt(value => value + 1);
    window.addEventListener('focus', focus);
    return () => { controller.abort(); clearTimeout(timer); window.removeEventListener('focus', focus); };
  }, [ready, base, options, report, cursors, index, filter, attempt]);
  useEffect(() => { setPartial(false); }, [preview?.id]);

  const act = async (operation, message) => {
    if (mutation.current) return;
    mutation.current = true; setBusy(true); setError(''); setNotice('');
    try {
      await operation();
      if (mounted.current) { setNotice(message); setAttempt(value => value + 1); }
    } catch (err) { if (mounted.current) report(err); }
    finally { mutation.current = false; if (mounted.current) setBusy(false); }
  };
  const authorize = () => {
    if (mutation.current) return;
    const popup = window.open('about:blank', '_blank');
    if (!popup) { setError('Permite abrir una pestaña para autorizar Google.'); return; }
    popup.opener = null;
    act(async () => {
      try {
        const { data } = await axios.post(`${base}/drive/autorizacion`, {}, { ...options(), withCredentials: true });
        const url = new URL(data.authorizationUrl);
        if (url.protocol !== 'https:' || url.hostname !== 'accounts.google.com' || url.username || url.password) throw new Error('Invalid authorization URL');
        popup.location.href = url.href;
      } catch (err) { popup.close(); throw err; }
    }, 'Completa la autorización en Google. Al finalizar, cierra esa pestaña y vuelve a actualizar el estado.');
  };
  const startJob = async (route, body) => {
    const { data } = await axios.post(`${base}/${route}`, body, options());
    if (data.job && mounted.current) {
      setJobs(values => [data.job, ...values.filter(job => job.id !== data.job.id)]);
      if (data.job.type === 'inventory') setPreview(data.job);
    }
  };
  const pendingJob = jobs.some(active);
  const usablePreview = preview && (preview.status === 'completed' || (preview.status === 'completed_with_errors' && partial));

  return <section aria-labelledby="drive-heading" className="space-y-5">
    <h2 id="drive-heading" className="text-xl font-semibold">Documentos de Drive</h2>
    <p>Conecta Google Drive, revisa el inventario y carga los PDFs originales al Vector Store del expediente.</p>
    {loading && <p role="status">Comprobando configuración del expediente…</p>}
    {error && <p role="alert" className="p-4 bg-red-50 text-red-800 rounded-lg">{error}</p>}
    {notice && <p role="status" className="p-4 bg-green-50 text-green-800 rounded-lg">{notice}</p>}
    {denied && <p role="alert">Tu cuenta administra el panel, pero no tiene permiso sobre el expediente. Configura su correo en EXPEDIENTES_ADMIN_EMAILS y actualiza el entorno del backend.</p>}
    {!loading && discovery && !discovery.enabled && <p>El módulo de expedientes está desactivado. Configura EXPEDIENTES_ENABLED en el backend.</p>}
    {!loading && config && !denied && <>
      <p><strong>Origen:</strong> {config.source.rootId || 'Sin configurar'}<br /><strong>Destino:</strong> {config.vectorStore.id}</p>
      {!discovery.syncEnabled && <p>La sincronización está desactivada. Completa el puente Apps Script, el directorio persistente y EXPEDIENTES_SYNC_ENABLED en el backend.</p>}
    </>}
    <button className={button} disabled={busy || loading} onClick={() => { setError(''); setAttempt(value => value + 1); }}>Actualizar estado</button>
    {ready && <>
      <p><strong>Google Drive:</strong> {transport === 'apps_script' ? 'Puente Apps Script configurado; el acceso se comprueba al ejecutar el script'
        : (connected ? 'Cuenta autorizada (acceso pendiente de comprobar en inventario)' : 'Sin autorizar')}</p>
      <div className="flex flex-wrap gap-3">
        {transport !== 'apps_script' && <button className={button} disabled={busy} onClick={authorize}>{connected ? 'Volver a autorizar Google' : 'Autorizar Google Drive'}</button>}
        <button className={button} disabled={busy} onClick={() => act(async () => {
          const { data } = await axios.get(`${base}/destino`, options()); setTarget(data);
        }, 'Destino comprobado.')}>Comprobar Vector Store</button>
        {transport !== 'apps_script' && <button className={button} disabled={busy || !connected || pendingJob} onClick={() => act(async () => {
          await startJob('sincronizaciones/vista-previa', {}); setPartial(false);
        }, 'Inventario solicitado. El worker lo ejecutará en segundo plano.')}>Crear inventario</button>}
      </div>
      {transport === 'apps_script' && <p className="rounded-lg bg-blue-50 p-4">Para actualizar el inventario ejecuta <code>iniciarInventario</code> en el proyecto Apps Script. La pantalla mostrará aquí los lotes recibidos.</p>}
      {target && <p role="status">Destino verificado: {target.name || target.id} · {target.status}{target.fileCounts ? ` · ${target.fileCounts.completed || 0} archivos indexados en el store` : ''}</p>}
      <div className="border rounded-lg p-4 space-y-3">
        <h3 className="font-semibold">Cargar PDFs del último inventario</h3>
        <p>{preview ? `Inventario ${preview.id}: ${label(preview.status)} · ${preview.observed} elementos observados · ${preview.errorCount} errores.` : 'Primero crea y termina un inventario.'}</p>
        {preview?.status === 'completed_with_errors' && <label className="block"><input type="checkbox" checked={partial} onChange={event => setPartial(event.target.checked)} /> Acepto cargar únicamente los archivos del inventario parcial, con carpetas o documentos pendientes.</label>}
        <p className="text-sm text-gray-600">Se cargarán los PDFs elegibles. Los ya indexados se reutilizan; los demás formatos quedan registrados. La carga no activa documentos en el chat.</p>
        {transport === 'apps_script' && <label className="block">Máximo de PDFs en este lote
          <input type="number" min="1" max="100" value={maxDocuments} onChange={event => setMaxDocuments(Number(event.target.value))}
            className="border rounded-lg p-2 ml-2 w-24" />
        </label>}
        <button className={`${button} bg-blue-700 text-white`} disabled={busy || !connected || !target || !usablePreview || pendingJob}
          onClick={() => act(() => startJob('sincronizaciones', { previewId: preview.id, allowPartial: partial,
            ...(transport === 'apps_script' ? { maxDocuments } : {}) }), transport === 'apps_script'
            ? 'Selección creada. Ejecuta transferirPendientes en Apps Script; después el worker cargará los PDFs recibidos.'
            : 'Carga solicitada. Puedes seguir su avance en los trabajos.')}>Cargar PDFs a OpenAI</button>
        {!target && <p className="text-sm">Comprueba el Vector Store antes de habilitar la carga.</p>}
      </div>
      <h3 className="text-lg font-semibold">Trabajos recientes</h3>
      <p className="text-sm text-gray-600">El estado se actualiza cada cinco segundos. Los trabajos en cola requieren el worker activo. Pausar se aplica al terminar el paso en curso; puedes cerrar esta pantalla sin detener la carga.</p>
      {!jobs.length && <p>No hay trabajos todavía.</p>}
      {jobs.map(job => <article key={job.id} className="border rounded-lg p-4 space-y-2">
        <h4 className="font-semibold">{job.type === 'inventory' ? 'Inventario' : 'Carga PDF'} · {label(job.status)}{job.pauseRequested && job.status === 'running' ? ' · Pausa solicitada' : ''}</h4>
        <p className="text-sm break-all">{job.id} · {job.type === 'inventory' ? `${job.observed} elementos observados` : `${job.processed} de ${job.selected} procesados`} · {job.errorCount} errores</p>
        {job.failure && <p role="alert">{reason(job.failure.code || job.failure)}</p>}
        {job.nextStep && <p className="text-sm">Siguiente paso: {reason(job.nextStep)}</p>}
        {Boolean(job.errors?.length) && <details><summary>Ver errores registrados</summary><ul className="list-disc pl-5">{job.errors.map((item, i) => <li key={i}>{item.documentId || 'Trabajo'}: {reason(item.code)}</li>)}</ul></details>}
        {['running', 'queued'].includes(job.status) && <button className={button} disabled={busy || job.pauseRequested} onClick={() => act(() => axios.post(`${base}/trabajos/${encodeURIComponent(job.id)}/pausa`, {}, options()), 'Pausa solicitada.')}>Pausar</button>}
        {['paused', 'failed', 'completed_with_errors'].includes(job.status) && <button className={button} disabled={busy} onClick={() => act(() => axios.post(`${base}/trabajos/${encodeURIComponent(job.id)}/reanudar`, {}, options()), 'Reanudación solicitada.')}>Reanudar</button>}
      </article>)}
      <h3 className="text-lg font-semibold">Inventario de documentos</h3>
      <p className="text-sm">{page.counts.map(item => `${label(item.status)}: ${item.count}`).join(' · ') || 'Sin documentos inventariados'}</p>
      <label className="block">Filtrar por estado <select className="border rounded-lg p-2 ml-2" value={filter} onChange={event => { setFilter(event.target.value); setIndex(0); setCursors(['']); }}>
        <option value="">Todos</option>{Object.keys(states).filter(value => !['queued', 'running', 'paused', 'failed', 'completed', 'completed_with_errors'].includes(value)).map(value => <option value={value} key={value}>{label(value)}</option>)}
      </select></label>
      <div className="overflow-x-auto"><table className="w-full text-left text-sm">
        <caption className="sr-only">Documentos inventariados, página {index + 1}</caption>
        <thead><tr>{['Documento', 'Estado', 'Motivo', 'Original'].map(value => <th className="p-2" key={value}>{value}</th>)}</tr></thead>
        <tbody>{page.documents.map(doc => <tr className="border-t" key={doc.id}>
          <td className="p-2 break-all">{doc.name}<span className="block text-xs text-gray-600">{doc.path?.join(' / ')}</span></td>
          <td className="p-2">{label(doc.status)}</td><td className="p-2">{reason(doc.reason) || '—'}</td>
          <td className="p-2">{safeDriveUrl(doc.originalUrl) && <a href={safeDriveUrl(doc.originalUrl)} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Abrir en Drive</a>}</td>
        </tr>)}</tbody>
      </table></div>
      <div className="flex gap-3 items-center">
        <button className={button} disabled={pageLoading || !index} onClick={() => { setPageLoading(true); setIndex(value => value - 1); }}>Anterior</button><span>Página {index + 1}</span>
        <button className={button} disabled={pageLoading || !page.nextCursor} onClick={() => { setPageLoading(true); setCursors(values => [...values.slice(0, index + 1), page.nextCursor]); setIndex(value => value + 1); }}>Siguiente</button>
      </div>
    </>}
  </section>;
}
